import { xMean } from 'ml-spectra-processing';
import { assert } from "./assert.js";
/**
 * Build an optimization layout mapping actual per-peak parameter slots
 * to optimizer variables. The layout describes slots, grouped/shared
 * variables, variable bounds/initials, and provides a helper to
 * materialize actual peak parameter values from a variable vector.
 * @param internalPeaks - normalized internal peaks with parameter indices
 * @param peaks - original peak objects (for per-peak optimize flags)
 * @param options - user `OptimizeOptions`, may contain `linkedParameters`
 * @param yScale - y normalization factor (used when converting offsets)
 * @returns an `OptimizationLayout` describing variables and slots
 */
export function buildOptimizationLayout(internalPeaks, peaks, options, yScale = 1) {
    const slots = buildParameterSlots(internalPeaks, peaks, options);
    const variables = buildOptimizationVariables(slots, options.linkedParameters, yScale);
    const variableMin = new Float64Array(variables.length);
    const variableMax = new Float64Array(variables.length);
    const variableInit = new Float64Array(variables.length);
    const variableGrad = new Float64Array(variables.length);
    const freeIndices = [];
    for (let i = 0; i < variables.length; i++) {
        const variable = variables[i];
        variableMin[i] = variable.min;
        variableMax[i] = variable.max;
        variableInit[i] = variable.init;
        variableGrad[i] = variable.gradientDifference;
        if (variable.optimize) {
            freeIndices.push(i);
        }
    }
    return {
        slots,
        variables,
        freeIndices,
        variableMin,
        variableMax,
        variableInit,
        variableGrad,
        variableToPeakValues(variableValues) {
            const actualValues = new Array(slots.length);
            for (let i = 0; i < variables.length; i++) {
                const variableValue = variableValues[i];
                const members = variables[i].members;
                for (const member of members) {
                    actualValues[member.actualIndex] =
                        variableValue * member.factor + member.offset;
                }
            }
            return actualValues;
        },
    };
}
/**
 * Builds concrete parameter slots for each peak parameter.
 * @param internalPeaks - normalized peaks containing parameter metadata
 * @param peaks - original peaks used to resolve optimize flags
 * @param options - optimization options with parameter settings
 * @returns flattened parameter slots across all peaks
 */
function buildParameterSlots(internalPeaks, peaks, options) {
    const slots = [];
    for (let peakIndex = 0; peakIndex < internalPeaks.length; peakIndex++) {
        const internalPeak = internalPeaks[peakIndex];
        for (let i = 0; i < internalPeak.parameters.length; i++) {
            const parameter = internalPeak.parameters[i];
            slots.push({
                actualIndex: internalPeak.fromIndex + i,
                peakIndex,
                peakId: internalPeak.id,
                parameter,
                init: internalPeak.propertiesValues.init[i],
                min: internalPeak.propertiesValues.min[i],
                max: internalPeak.propertiesValues.max[i],
                gradientDifference: internalPeak.propertiesValues.gradientDifference[i],
                optimize: getOptimizeFlag(peaks[peakIndex], parameter, options),
            });
        }
    }
    return slots;
}
/**
 * Builds optimization variables from concrete parameter slots.
 * @param slots - flattened per-peak parameter slots
 * @param linkedParameters - optional linked parameter groups
 * @param yScale - y normalization factor for y-offset conversion
 * @returns sorted optimization variables ready for the optimizer
 */
function buildOptimizationVariables(slots, linkedParameters, yScale) {
    const groupedActualIndices = new Set();
    const variables = [];
    const slotLookup = new Map();
    const idToIndices = new Map();
    for (const slot of slots) {
        slotLookup.set(getSlotKey(slot.peakIndex, slot.parameter), slot);
        if (slot.peakId) {
            const indices = idToIndices.get(slot.peakId) ?? [];
            if (!indices.includes(slot.peakIndex)) {
                indices.push(slot.peakIndex);
            }
            idToIndices.set(slot.peakId, indices);
        }
    }
    for (const linkedParameter of linkedParameters ?? []) {
        variables.push(buildLinkedVariable(linkedParameter, slotLookup, groupedActualIndices, idToIndices, yScale));
    }
    for (const slot of slots) {
        if (groupedActualIndices.has(slot.actualIndex)) {
            continue;
        }
        variables.push({
            sortKey: slot.actualIndex,
            parameter: slot.parameter,
            init: slot.init,
            min: slot.min,
            max: slot.max,
            gradientDifference: slot.gradientDifference,
            optimize: slot.optimize,
            members: [
                {
                    actualIndex: slot.actualIndex,
                    peakIndex: slot.peakIndex,
                    parameter: slot.parameter,
                    factor: 1,
                    offset: 0,
                },
            ],
        });
    }
    variables.sort((a, b) => a.sortKey - b.sortKey);
    return variables.map(({ sortKey: _sortKey, ...variable }) => variable);
}
function buildLinkedVariable(linkedParameter, slotLookup, groupedActualIndices, idToIndices, yScale) {
    if (linkedParameter.peaks.length === 0) {
        throw new Error(`Linked parameter for ${linkedParameter.parameter} must contain at least one peak`);
    }
    const resolvedMembers = linkedParameter.peaks.map((peak) => {
        const slot = resolveLinkedSlot(peak, linkedParameter.parameter, slotLookup, idToIndices);
        if (groupedActualIndices.has(slot.actualIndex)) {
            throw new Error(`Peak ${String(peak.id)} parameter ${linkedParameter.parameter} is already linked`);
        }
        return {
            slot,
            factor: getFactor(peak, linkedParameter.parameter),
            offset: getOffset(peak, linkedParameter.parameter, yScale),
        };
    });
    const memberActualIndices = new Set();
    for (const member of resolvedMembers) {
        if (memberActualIndices.has(member.slot.actualIndex)) {
            throw new Error(`Linked parameter for ${linkedParameter.parameter} contains the same peak more than once`);
        }
        memberActualIndices.add(member.slot.actualIndex);
    }
    const firstMember = resolvedMembers[0];
    let sharedMin = Number.NEGATIVE_INFINITY;
    let sharedMax = Number.POSITIVE_INFINITY;
    const optimize = firstMember.slot.optimize;
    const sharedInitCandidates = [];
    for (const member of resolvedMembers) {
        if (member.slot.optimize !== optimize) {
            throw new Error(`Linked parameter ${linkedParameter.parameter} must use a consistent optimize flag across all members`);
        }
        if (member.slot.min > member.slot.max) {
            throw new Error(`Linked parameter ${linkedParameter.parameter} has incompatible bounds across its members`);
        }
        const variableBounds = getMemberVariableBounds(member);
        sharedMin = Math.max(sharedMin, variableBounds.min);
        sharedMax = Math.min(sharedMax, variableBounds.max);
        sharedInitCandidates.push((member.slot.init - member.offset) / member.factor);
    }
    if (sharedMin > sharedMax) {
        throw new Error(`Linked parameter ${linkedParameter.parameter} has incompatible bounds across its members`);
    }
    for (const member of resolvedMembers) {
        groupedActualIndices.add(member.slot.actualIndex);
    }
    return {
        sortKey: Math.min(...resolvedMembers.map((member) => member.slot.actualIndex)),
        parameter: linkedParameter.parameter,
        init: xMean(sharedInitCandidates),
        min: sharedMin,
        max: sharedMax,
        gradientDifference: Math.min(...resolvedMembers.map((m) => Math.abs(m.slot.gradientDifference))),
        optimize,
        members: resolvedMembers.map((member) => ({
            actualIndex: member.slot.actualIndex,
            peakIndex: member.slot.peakIndex,
            parameter: member.slot.parameter,
            factor: member.factor,
            offset: member.offset,
        })),
    };
}
function resolveLinkedSlot(peak, parameter, slotLookup, idToIndices) {
    const peakIndex = typeof peak.id === 'number'
        ? peak.id
        : resolvePeakIndexById(peak.id, idToIndices);
    if (!Number.isInteger(peakIndex) || peakIndex < 0) {
        throw new Error(`Invalid peak reference ${String(peak.id)}`);
    }
    const slot = slotLookup.get(getSlotKey(peakIndex, parameter));
    if (!slot) {
        throw new Error(`Unknown parameter ${parameter} for peak ${String(peak.id)}`);
    }
    return slot;
}
function resolvePeakIndexById(peakId, idToIndices) {
    const indices = idToIndices.get(peakId);
    if (!indices || indices.length === 0) {
        throw new Error(`Unknown peak id ${peakId}`);
    }
    if (new Set(indices).size > 1) {
        throw new Error(`Peak id ${peakId} is ambiguous because it is used by multiple peaks`);
    }
    return indices[0];
}
function getFactor(peak, parameter) {
    const factor = peak.factor ?? 1;
    if (!Number.isFinite(factor) || factor === 0) {
        throw new Error(`Linked parameter ${parameter} must use a non-zero finite factor`);
    }
    return factor;
}
function getOffset(peak, parameter, yScale) {
    const offset = peak.offset ?? 0;
    if (!Number.isFinite(offset)) {
        throw new Error(`Linked parameter ${parameter} must use a finite offset`);
    }
    if (parameter === 'y') {
        return offset / yScale;
    }
    return offset;
}
function getMemberVariableBounds(member) {
    const transformedMin = (member.slot.min - member.offset) / member.factor;
    const transformedMax = (member.slot.max - member.offset) / member.factor;
    return {
        min: Math.min(transformedMin, transformedMax),
        max: Math.max(transformedMin, transformedMax),
    };
}
function getOptimizeFlag(peak, parameter, options) {
    assert(peak);
    let optimizeFlag = true;
    const perPeakParam = peak.parameters?.[parameter];
    const globalParam = options.parameters?.[parameter];
    if (perPeakParam?.optimize !== undefined) {
        if (typeof perPeakParam.optimize === 'function') {
            optimizeFlag = perPeakParam.optimize(peak);
        }
        else {
            const { optimize = true } = perPeakParam;
            optimizeFlag = optimize;
        }
    }
    else if (globalParam?.optimize !== undefined) {
        if (typeof globalParam.optimize === 'function') {
            optimizeFlag = globalParam.optimize(peak);
        }
        else {
            const { optimize = true } = globalParam;
            optimizeFlag = optimize;
        }
    }
    return optimizeFlag;
}
function getSlotKey(peakIndex, parameter) {
    return `${peakIndex}:${parameter}`;
}
//# sourceMappingURL=buildOptimizationLayout.js.map